import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Button, CircularProgress, IconButton, type SxProps, type Theme, Tooltip } from '@mui/material';

import { useClaimScrubberWithUI } from '../../store/hooks';
import type { Claim } from '../../types';

interface ClaimScrubButtonProps {
  /** The claim ID to scrub */
  claimId: string | undefined;
  /** Whether to render as IconButton instead of full Button */
  asIconButton?: boolean;
  /** Optional custom button text (for full button mode) */
  buttonText?: string;
  /** Optional button variant (for full button mode) */
  variant?: 'text' | 'outlined' | 'contained';
  /** Optional button color */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Optional button/icon size */
  size?: 'small' | 'medium' | 'large';
  /** Icon size for IconButton mode */
  iconSize?: 'small' | 'medium' | 'large';
  /** Optional callback after scrub completes */
  onScrubComplete?: (result: Claim) => void;
  /** Optional custom styles */
  sx?: SxProps<Theme>;
}

/**
 * Standalone Claim Scrub Button Component
 * 
 * This component is completely decoupled and can be placed anywhere.
 * It uses RTK Query for server state and Redux for UI state.
 * Can render as either a full button or an icon button.
 */
export const ClaimScrubButton: React.FC<ClaimScrubButtonProps> = ({
  claimId,
  asIconButton = false,
  buttonText = 'Check Claim',
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  iconSize = 'medium',
  onScrubComplete,
  sx,
}) => {
  const { scrubClaim, isScrubbing } = useClaimScrubberWithUI();

  const handleClick = async () => {
    if (!claimId) {
      // Cannot scrub claim without ID - fail silently
      return;
    }

    const result = await scrubClaim(claimId);
    
    if (onScrubComplete && result.success && result.data) {
      onScrubComplete(result.data);
    }
  };

  const ButtonIcon = CheckCircleOutlineIcon;
  
  const iconSizeMap = {
    small: '1rem',
    medium: '1.2rem',
    large: '1.5rem',
  };

  // Render as IconButton
  if (asIconButton) {
    return (
      <Tooltip 
        title={!claimId ? 'Save claim before checking' : 'Validate claim for errors and warnings'}
        placement="bottom"
        arrow
      >
        <span>
          <IconButton
            onClick={handleClick}
            disabled={!claimId || isScrubbing}
            size={iconSize}
            sx={sx}
          >
            {isScrubbing ? (
              <CircularProgress size={iconSizeMap[iconSize]} />
            ) : (
              <ButtonIcon sx={{ fontSize: iconSizeMap[iconSize] }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Render as full Button
  return (
    <Tooltip 
      title={!claimId ? 'Save claim before checking' : 'Validate claim for errors and warnings'}
      placement="top"
    >
      <span>
        <Button
          variant={variant}
          color={color}
          size={size}
          onClick={handleClick}
          disabled={!claimId || isScrubbing}
          startIcon={
            isScrubbing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ButtonIcon />
            )
          }
          sx={{
            minWidth: 140,
            ...(sx as object),
          }}
        >
          {isScrubbing ? 'Checking...' : buttonText}
        </Button>
      </span>
    </Tooltip>
  );
};